import { Upload, Mic, Sparkles, Disc3 } from "lucide-react";
import { useState } from "react";
import { TrackLane } from "./TrackLane";
import { useDrag } from "./useDrag";
import { LoopBand } from "./LoopBand";

const Ruler = ({ duration, progress, onSeek, loop, onLoop }) => {
  const [temp, setTemp] = useState(null);
  const secs = Math.ceil(duration);
  const step = secs > 40 ? 5 : secs > 15 ? 2 : 1;
  const drag = useDrag({
    onDrag: (_, f0, f1) => setTemp({ start: Math.min(f0, f1) * duration, end: Math.max(f0, f1) * duration, enabled: true }),
    onEnd: (_, f0, f1) => {
      setTemp(null);
      onLoop(Math.min(f0, f1) * duration, Math.max(f0, f1) * duration);
    },
    onClick: onSeek,
  });
  return (
    <div className="flex h-7">
      <div className="w-52 shrink-0 eyebrow self-end pb-0.5 !text-[9px]">Drag ruler to set loop</div>
      <div data-testid="timeline-ruler" className="relative flex-1 border-b border-[#352d40] cursor-pointer touch-none select-none" {...drag}>
        <LoopBand loop={temp || loop} duration={duration} />
        {Array.from({ length: Math.floor(secs / step) + 1 }, (_, i) => i * step).map((s) => (
          <span key={s} className="absolute bottom-0 text-[10px] font-mono-vox text-stone-500 pl-1 border-l border-[#352d40] h-3 leading-none pointer-events-none" style={{ left: `${(s / duration) * 100}%` }}>
            {s}s
          </span>
        ))}
        <div data-testid="timeline-playhead" className="absolute top-0 bottom-0 w-0 pointer-events-none" style={{ left: `${progress * 100}%` }}>
          <div className="w-2.5 h-2.5 -ml-[5px] rotate-45 bg-[#EF4444] shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
        </div>
      </div>
    </div>
  );
};

const Empty = ({ onDemo, onRecord, onUpload, onSong }) => (
  <div data-testid="lanes-empty" className="flex-1 flex flex-col items-center justify-center text-center p-10 border border-dashed border-[#352d40] arch-top bg-[#0d0b0f]">
    <p className="eyebrow text-[#D99B43]">⬥ The altar is silent ⬥</p>
    <h2 className="font-gothic text-2xl uppercase tracking-wide text-stone-200 mt-4">Offer a voice</h2>
    <p className="font-cormorant italic text-lg text-stone-400 mt-2 max-w-md">
      Drag & drop MP3/WAV/M4A files you own, import a song to sing over, record from your microphone, or summon the demo chant to begin.
    </p>
    <div className="flex flex-wrap justify-center gap-3 mt-8">
      <button data-testid="empty-song-btn" onClick={onSong} className="btn-gothic"><Disc3 size={14} /> Import song</button>
      <button data-testid="empty-record-btn" onClick={onRecord} className="btn-crimson"><Mic size={14} /> Record</button>
      <button data-testid="empty-upload-btn" onClick={onUpload} className="btn-gothic"><Upload size={14} /> Upload</button>
      <button data-testid="empty-demo-btn" onClick={onDemo} className="btn-gothic"><Sparkles size={14} /> Demo chant</button>
    </div>
  </div>
);

export const Lanes = ({ tracks, buffers, duration, time, selectedId, onSelect, onUpdate, onDelete, onSeek, loop, onLoop, onDragEnd, ...empty }) => {
  const progress = duration ? Math.min(1, time / duration) : 0;
  if (!tracks.length) return <Empty {...empty} />;
  return (
    <div data-testid="multitrack-container" className="flex flex-col gap-2">
      <Ruler duration={duration} progress={progress} onSeek={onSeek} loop={loop} onLoop={onLoop} />
      {tracks.map((t, i) => (
        <TrackLane
          key={t.id}
          track={t}
          index={i}
          buffer={buffers.get(t.id)}
          duration={duration}
          progress={progress}
          selected={t.id === selectedId}
          onSelect={() => onSelect(t.id)}
          onUpdate={(patch) => onUpdate(t.id, patch)}
          onDelete={() => onDelete(t.id)}
          onSeek={onSeek}
          onDragEnd={onDragEnd}
          loop={loop}
        />
      ))}
    </div>
  );
};
